import React from "react";
import { MessageCircle, Phone, Mail, MapPin, Clock, Linkedin } from "lucide-react";

import { PageHero } from "../components/site/PageHero";
import { ContactQuote } from "../components/site/ContactQuote";
import { usePageMeta } from "../hooks/usePageMeta";

const WA_URL = `https://wa.me/32466324304?text=${encodeURIComponent(
  "Hallo, ik wil graag een offerte voor schoonmaakdiensten."
)}`;

export default function ContactPage() {
  usePageMeta({
    title: "Contact — Vraag uw schoonmaak-offerte aan | Dila Group BV Antwerpen",
    description:
      "Neem contact op met Dila Group BV in Antwerpen. Telefoon 0466 32 43 04, e-mail info@dilagroupservices.be, WhatsApp of vul het offerteformulier in. 24/7 bereikbaar.",
    path: "/contact",
  });

  return (
    <>
      <PageHero
        breadcrumb="Contact"
        eyebrow="Contact & Offerte"
        title="Neem contact op — wij staan voor u klaar."
        intro="Liever bellen, WhatsApp'en of een formulier invullen? Kies wat voor u het makkelijkst is. We reageren altijd binnen 24 uur — vaak veel sneller."
      />

      {/* Quick contact methods */}
      <section className="py-16 bg-white" data-testid="contact-methods">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <a
            href="tel:0466324304"
            data-testid="contact-method-phone"
            className="group rounded-3xl bg-white border border-slate-200 p-6 hover:border-[#0284C7]/40 hover:shadow-md transition-all"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-50 text-[#0284C7] group-hover:bg-[#0284C7] group-hover:text-white transition-colors">
              <Phone size={20} />
            </span>
            <h3 className="mt-4 font-display font-bold text-[#0F172A]">Bellen</h3>
            <p className="mt-1 font-display text-lg font-extrabold text-[#0284C7]">0466 32 43 04</p>
            <p className="text-xs text-slate-500 mt-1">24/7 bereikbaar</p>
          </a>

          <a
            href={WA_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-method-whatsapp"
            className="group rounded-3xl bg-white border border-slate-200 p-6 hover:border-[#25D366]/50 hover:shadow-md transition-all"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-[#25D366] group-hover:bg-[#25D366] group-hover:text-white transition-colors">
              <MessageCircle size={20} />
            </span>
            <h3 className="mt-4 font-display font-bold text-[#0F172A]">WhatsApp</h3>
            <p className="mt-1 font-display text-lg font-extrabold text-[#25D366]">Direct chatten</p>
            <p className="text-xs text-slate-500 mt-1">Meestal antwoord binnen 15 min</p>
          </a>

          <a
            href="mailto:info@dilagroupservices.be"
            data-testid="contact-method-email"
            className="group rounded-3xl bg-white border border-slate-200 p-6 hover:border-[#0284C7]/40 hover:shadow-md transition-all"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-50 text-[#0284C7] group-hover:bg-[#0284C7] group-hover:text-white transition-colors">
              <Mail size={20} />
            </span>
            <h3 className="mt-4 font-display font-bold text-[#0F172A]">E-mail</h3>
            <p className="mt-1 font-display text-sm font-bold text-[#0F172A] break-all">info@dilagroupservices.be</p>
            <p className="text-xs text-slate-500 mt-1">Antwoord binnen 24u</p>
          </a>

          <div
            data-testid="contact-method-address"
            className="rounded-3xl bg-white border border-slate-200 p-6"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-[#16A34A]">
              <MapPin size={20} />
            </span>
            <h3 className="mt-4 font-display font-bold text-[#0F172A]">Adres</h3>
            <p className="mt-1 font-display text-sm font-bold text-[#0F172A]">Brederodestraat 94</p>
            <p className="text-xs text-slate-500">2018 Antwerpen, België</p>
          </div>
        </div>
      </section>

      {/* Main quote form (reuse ContactQuote component) */}
      <ContactQuote />

      {/* Hours + LinkedIn + Map */}
      <section className="py-20 sm:py-28 bg-white" data-testid="contact-extra">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div className="space-y-6">
            <div className="rounded-3xl bg-[#F4F7F6] border border-slate-200 p-7">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-[#16A34A]">
                <Clock size={20} />
              </span>
              <h3 className="mt-4 font-display text-xl font-bold text-[#0F172A]">Openingsuren</h3>
              <p className="mt-2 text-slate-600">
                <strong className="text-[#0F172A]">Open 24 uur</strong> — 7 dagen op 7.
                Voor dringende interventies bellen wij u terug binnen 15 minuten.
              </p>
            </div>

            <a
              href="https://www.linkedin.com/in/funda-mehmed-bb05653b6"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-extra-linkedin"
              className="block rounded-3xl bg-[#F4F7F6] border border-slate-200 p-7 hover:border-[#0A66C2]/40 hover:bg-white transition-all"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0A66C2] text-white">
                <Linkedin size={20} strokeWidth={2.5} />
              </span>
              <h3 className="mt-4 font-display text-xl font-bold text-[#0F172A]">Connect op LinkedIn</h3>
              <p className="mt-2 text-slate-600">
                Volg <strong className="text-[#0F172A]">Funda Mehmed</strong> en blijf op de hoogte van Dila Group BV.
              </p>
            </a>
          </div>

          <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-md min-h-[360px]">
            <iframe
              title="Dila Group BV — Brederodestraat 94, 2018 Antwerpen"
              src="https://www.google.com/maps?q=Brederodestraat+94,+2018+Antwerpen&output=embed"
              width="100%"
              height="100%"
              className="min-h-[360px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </div>
        </div>
      </section>
    </>
  );
}
